import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from 'react';
import {Add, InfoOutlined} from '@mui/icons-material';
import {Box, Tooltip, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {
    ListPageShell,
    PageActions,
    SearchContainer,
} from '../../../../../components/page-shell';
import {Tab, Tabs} from '../../../../../components/base/Tabs';
import SearchInput from '../../../../../components/base/input/search_input';
import CreateUserOnlineProfileDialog from '../../../../../components/dialogs/create-user-online-profile';
import AssignUserToCustomerDialog from '../../../../../components/dialogs/assign-user-to-customer';
import {useDataGridSavedState} from '../../../../../../packages/data-grid';
import {CustomerMemberShipNames, DefaultPaginationInfo} from '../../../../../../core/constants/enums';
import Api from '../../../../../../core/services/api/api';
import {formatPhoneNumber} from '../../../../../../core/services/utils/utils';
import DateUtils from '../../../../../../core/services/date-utils';
import useSearch from '../../../../../hooks/use-search';
import useIsMounted from '../../../../../hooks/use-is-mounted';
import useAccessLevels from '../../../../../hooks/use-access-levels';
import useQuery from '../../../../../hooks/use-query';
import {useConfigureNotebook} from '../../../../../hooks/use-notebook-config';
import useBehavioralAnalyticsTool from '../../../../../hooks/use-behavioural-analytics-tool';
import {setReduxDialogState} from '../../../../../redux/slices/dialogs/actions';
import ReduxStoreRoutes from '../../../../../../core/models/static/routes/redux-store-routes';
import {
    ALL_TAB,
    dataGridSaveConfigs,
    resolveAssignedCustomerExportValue,
    resolveMembershipTitle,
    resolveStatusLabel,
} from './sections/grid-shared';
import {resolveMembershipStatusColor} from './sections/membership-status-chip';
import MembersAllGridSection from './sections/all-grid';
import MembersActiveGridSection from './sections/active-grid';
import MembersOnHoldGridSection from './sections/on-hold-grid';
import MembersInactiveGridSection from './sections/inactive-grid';
import MembersGenericGridSection from './sections/generic-grid';
import ResendActivationDialog from './sections/resend-activation-dialog';

const defaultTab = ALL_TAB;
const exportingChunkSize = 100;

/**
 * Tab label with a status color dot, title, and info icon tooltip.
 * @param {object} props
 * @param {string} props.label
 * @param {string} props.description
 * @param {string} props.status
 */
const MembersTabLabel = ({label, description, status}) => {
    const {dot} = resolveMembershipStatusColor(status);

    /**
     * Prevents the info icon click from also selecting the tab.
     * @type {(function(React.MouseEvent): void)}
     */
    const stopTabSelect = useCallback((event) => {
        event.stopPropagation();
        event.preventDefault();
    }, []);

    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
            }}
        >
            <Box
                component="span"
                aria-hidden
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: dot,
                    flexShrink: 0,
                }}
            />
            <Box component="span">{label}</Box>
            {
                !!description &&
                <Tooltip
                    arrow
                    enterTouchDelay={0}
                    title={(
                        <Typography variant="body2" component="p" sx={{maxWidth: 320, py: 0.25}}>
                            {description}
                        </Typography>
                    )}
                >
                    <Box
                        component="span"
                        onClick={stopTabSelect}
                        onMouseDown={stopTabSelect}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            color: 'text.secondary',
                            lineHeight: 0,
                        }}
                    >
                        <InfoOutlined sx={{fontSize: 18}}/>
                    </Box>
                </Tooltip>
            }
        </Box>
    );
};

/**
 * Tabbed members list: All + each membership type from GetAllUserSearchFormParams.
 * Tab / search wiring mirrors quotes and purchases list architecture.
 */
const CustomersMembersListView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.customers.members'});
    const dispatch = useDispatch();
    const [query] = useQuery();
    const operatorAccessLevels = useAccessLevels();
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('customers-members-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const [members, setMembers] = useState([]);
    const [memberShips, setMemberShips] = useState([]);
    const [countries, setCountries] = useState([]);
    const [formLoading, setFormLoading] = useState(true);
    const [openAddUser, setOpenAddUser] = useState(false);
    const [assignDialog, setAssignDialog] = useState({open: false, member: null});
    const [hasB2cWebsite, setHasB2cWebsite] = useState(false);
    const [resendDialog, setResendDialog] = useState({open: false, member: null, members: null});
    const [updatedTab, setUpdatedTab] = useState(query?.tab ?? defaultTab);

    const canCreateMember = operatorAccessLevels.packages.base.createCustomerMember;
    const canOpenProfile = operatorAccessLevels.packages.base.customerDetails;

    /**
     * Configures notebook tags and behavioral analytics for the unified members page.
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.customersMembers);
        configureBehavioralTool((tags) => tags.customersMembers);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Loads membership types and countries for tabs / OnHold address columns / assign dialog.
     * @type {function(): Promise<void>}
     */
    const getFormData = useCallback(async () => {
        setFormLoading(true);
        const response = await Api.getUserSearchForm();
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag) {
            setMemberShips(response.data?.memberShips ?? []);
            setCountries(response.data?.countries ?? []);
        }
        setFormLoading(false);
    }, [isMounted]);

    useEffect(() => {
        void getFormData();
    }, [getFormData]);

    /**
     * Loads whether this partner has a B2C retail user. The resend-activation action stays hidden when it does not.
     * @type {function(): Promise<void>}
     */
    const loadB2cWebsiteFlag = useCallback(async () => {
        const response = await Api.getPartnerRetailUser({showErrorToast: false});
        if (!isMounted()) {
            return;
        }
        setHasB2cWebsite(!!(response?.resultFlag && response.data?.userId));
    }, [isMounted]);

    /**
     * Fetches the B2C retail-user flag once when the members page mounts.
     */
    useEffect(() => {
        void loadB2cWebsiteFlag();
    }, [loadB2cWebsiteFlag]);

    const membershipNames = useMemo(
        () => (memberShips ?? []).map((m) => m.name).filter(Boolean),
        [memberShips],
    );

    const membershipFallbackTitles = useMemo(
        () => (memberShips ?? []).reduce((acc, membership) => {
            if (membership?.name) {
                acc[membership.name] = membership.title ?? membership.name;
            }
            return acc;
        }, {}),
        [memberShips],
    );

    /**
     * Resolves the display title for a tab / status, preferring our i18n copy.
     * @type {(function(string): string)}
     */
    const getMembershipTitle = useCallback((statusOrTab) => (
        resolveMembershipTitle(statusOrTab, t, membershipFallbackTitles[statusOrTab])
    ), [membershipFallbackTitles, t]);

    const [, allSortBy, allPageSize] = useDataGridSavedState({
        columns: [],
        ...dataGridSaveConfigs[ALL_TAB],
    });
    const [, activeSortBy, activePageSize] = useDataGridSavedState({
        columns: [],
        ...dataGridSaveConfigs[CustomerMemberShipNames.active],
    });
    const [, onHoldSortBy, onHoldPageSize] = useDataGridSavedState({
        columns: [],
        ...dataGridSaveConfigs[CustomerMemberShipNames.onHold],
    });
    const [, deactivatedSortBy, deactivatedPageSize] = useDataGridSavedState({
        columns: [],
        ...dataGridSaveConfigs[CustomerMemberShipNames.deactivated],
    });
    const [, onHoldCompanyLostSortBy, onHoldCompanyLostPageSize] = useDataGridSavedState({
        columns: [],
        ...dataGridSaveConfigs[CustomerMemberShipNames.onHoldCompanyLost],
    });
    const [, genericSortBy, genericPageSize] = useDataGridSavedState({
        columns: [],
        ...dataGridSaveConfigs.generic,
    });

    /**
     * Resolves saved sort / pageSize for the active tab (quotes-style).
     * @type {[any, number|undefined]}
     */
    const [dataGridSortBy, dataGridPageSize] = useMemo(() => {
        switch (updatedTab) {
            case ALL_TAB:
                return [allSortBy, allPageSize];
            case CustomerMemberShipNames.active:
                return [activeSortBy, activePageSize];
            case CustomerMemberShipNames.onHold:
                return [onHoldSortBy, onHoldPageSize];
            case CustomerMemberShipNames.deactivated:
                return [deactivatedSortBy, deactivatedPageSize];
            case CustomerMemberShipNames.onHoldCompanyLost:
                return [onHoldCompanyLostSortBy, onHoldCompanyLostPageSize];
            default:
                return [genericSortBy, genericPageSize];
        }
    }, [
        activePageSize,
        activeSortBy,
        allPageSize,
        allSortBy,
        deactivatedPageSize,
        deactivatedSortBy,
        genericPageSize,
        genericSortBy,
        onHoldCompanyLostPageSize,
        onHoldCompanyLostSortBy,
        onHoldPageSize,
        onHoldSortBy,
        updatedTab,
    ]);

    /**
     * Returns saved pageSize / orderBy for a tab when switching.
     * @type {(function(string): {pageSize: number, orderBy: any})}
     */
    const resolveTabGridState = useCallback((tab) => {
        switch (tab) {
            case ALL_TAB:
                return {pageSize: allPageSize, orderBy: allSortBy};
            case CustomerMemberShipNames.active:
                return {pageSize: activePageSize, orderBy: activeSortBy};
            case CustomerMemberShipNames.onHold:
                return {pageSize: onHoldPageSize, orderBy: onHoldSortBy};
            case CustomerMemberShipNames.deactivated:
                return {pageSize: deactivatedPageSize, orderBy: deactivatedSortBy};
            case CustomerMemberShipNames.onHoldCompanyLost:
                return {pageSize: onHoldCompanyLostPageSize, orderBy: onHoldCompanyLostSortBy};
            default:
                return {pageSize: genericPageSize, orderBy: genericSortBy};
        }
    }, [
        activePageSize,
        activeSortBy,
        allPageSize,
        allSortBy,
        deactivatedPageSize,
        deactivatedSortBy,
        genericPageSize,
        genericSortBy,
        onHoldCompanyLostPageSize,
        onHoldCompanyLostSortBy,
        onHoldPageSize,
        onHoldSortBy,
    ]);

    const initialPaginationInfo = useMemo(() => (dataGridPageSize
        ? {...DefaultPaginationInfo, pageSize: dataGridPageSize}
        : DefaultPaginationInfo), [dataGridPageSize]);

    /**
     * Prepares the SearchAllUsers request for the active tab and filters.
     * @type {(function(UseSearchFunctionArg): any)}
     */
    const prepareApiRequest = useCallback((args) => {
        let forApi = {};
        if (args.orderBy?.field) {
            forApi.orderByProperties = [{
                propertyName: args.orderBy.field,
                isDescending: args.orderBy.descending,
            }];
        }
        const tab = args.tab ?? ALL_TAB;
        const memberShipNames = tab === ALL_TAB
            ? membershipNames
            : [tab];
        forApi = {
            ...forApi,
            paginationInfo: args.paginationInfo,
            filters: {
                ...(args.filters ?? {}),
                memberShipNames,
                isOrderBySubmitDateADescending: false,
            },
        };
        return forApi;
    }, [membershipNames]);

    /**
     * Searches members for the current tab / keyword filters.
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        if (!membershipNames.length && (args.tab ?? ALL_TAB) === ALL_TAB) {
            if (!forExport) {
                setMembers([]);
                args.setPaginationInfo?.((prevState) => ({...prevState, length: 0}));
            }
            return;
        }
        const forApi = prepareApiRequest(args);
        const response = await Api.searchCurrentUsers(forApi, args.cancelTokenSource?.token);
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag && !forExport) {
            setMembers(response.data?.resultList ?? []);
            args.setPaginationInfo((prevState) => ({
                ...prevState,
                length: response?.data?.paginationInfo?.length ?? 0,
            }));
        }
        if (response?.resultFlag && forExport) {
            return response;
        }
    }, [isMounted, membershipNames.length, prepareApiRequest]);

    /**
     * useSearch callback for list fetching.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialSearchDataSettings = useMemo(() => ({
        tab: updatedTab,
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        disabled: formLoading || membershipNames.length === 0,
    }), [dataGridSortBy, formLoading, initialPaginationInfo, membershipNames.length, updatedTab]);

    const searchData = useSearch(search, initialSearchDataSettings);
    const {performSearch} = searchData;

    /**
     * Keeps local updatedTab in sync with useSearch tab (URL-backed).
     */
    useLayoutEffect(() => {
        setUpdatedTab(searchData.tab);
    }, [searchData.tab]);

    /**
     * Falls back invalid URL tabs to All once membership types are known.
     */
    useEffect(() => {
        if (formLoading || !membershipNames.length) {
            return;
        }
        const tab = searchData.tab ?? ALL_TAB;
        if (tab !== ALL_TAB && !membershipNames.includes(tab)) {
            searchData.setTab?.(ALL_TAB);
        }
    }, [formLoading, membershipNames, searchData]);

    /**
     * Updates the keyword filter used across all tabs.
     * @type {(function(string): void)}
     */
    const searchByKeyword = useCallback((keywords) => {
        searchData.setFilters((prevState) => ({
            ...prevState,
            keyword: keywords?.trim(),
        }));
    }, [searchData]);

    /**
     * Tab change: clear rows, set tab, swap pageSize / orderBy to that tab's saved grid state.
     * @type {(function(*, string): void)}
     */
    const onTabChanged = useCallback((_, newTab) => {
        setMembers([]);
        searchData.setTab(newTab);
        const {pageSize, orderBy} = resolveTabGridState(newTab);
        searchData.setPaginationInfo((prevState) => ({
            ...prevState,
            pageSize: pageSize ?? initialPaginationInfo.pageSize,
        }));
        searchData.setOrderBy(orderBy);
    }, [initialPaginationInfo.pageSize, resolveTabGridState, searchData]);

    /**
     * Formats export rows for the current tab.
     * @type {(function(any[]): {title: string, body: object[]})}
     */
    const formatExportingData = useCallback((list) => {
        const tab = searchData.tab ?? ALL_TAB;
        const exportTitle = tab === ALL_TAB
            ? t('export.all')
            : getMembershipTitle(tab);
        return {
            title: exportTitle,
            body: list?.map((item) => {
                const base = {
                    [t('export.status')]: resolveStatusLabel(
                        item,
                        t,
                        membershipFallbackTitles[item?.memberShipName],
                    ),
                    [t('export.name')]: `${item.firstName ?? ''} ${item.lastName ?? ''}`.trim(),
                    [t('export.email')]: item.email ?? '',
                    [t('export.phone')]: formatPhoneNumber(item.phone) ?? '',
                    [t('export.customer')]: resolveAssignedCustomerExportValue(item, t),
                    [t('export.registerDate')]: item.submitDateTime
                        ? DateUtils.serverToLocal(item.submitDateTime).format(DateUtils.dateFormat)
                        : '',
                    [t('export.latestLogin')]: item.lastLoginDate
                        ? DateUtils.serverToLocal(item.lastLoginDate).format(DateUtils.dateTimeFormat)
                        : '',
                };
                if (tab === CustomerMemberShipNames.onHold) {
                    return {
                        ...base,
                        [t('export.companyPhone')]: formatPhoneNumber(item.companyPhone ?? item.customer?.phone) ?? '',
                        [t('export.country')]: countries?.find((e) => e.id === item.province?.countryId)?.name ?? '',
                        [t('export.province')]: item.province?.name ?? '',
                        [t('export.city')]: item.city ?? '',
                        [t('export.address')]: item.address ?? '',
                        [t('export.postalCode')]: item.postalCode ?? '',
                    };
                }
                return base;
            }) ?? [],
        };
    }, [countries, getMembershipTitle, membershipFallbackTitles, searchData.tab, t]);

    /**
     * Exports the current filtered list to CSV in chunks.
     * @returns {Promise<DataGridExportInfo | undefined>}
     */
    const canExportCustomerMembersList = operatorAccessLevels.packages.base.exportCustomerMembersList;
    const exportToCsv = useCallback(async () => {
        const paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        const entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo}, true);
            if (!res?.resultFlag) {
                return;
            }
            if (!res?.data?.resultList?.length) {
                break;
            }
            entries.push(...res.data.resultList);
            if (!isMounted()) {
                return;
            }
            paginationInfo.currentPage++;
        }
        return formatExportingData(entries);
    }, [fetchEntries, formatExportingData, isMounted, searchData]);

    /**
     * Refreshes the list after create / mutation success.
     * @type {function(): void}
     */
    const onAddSuccess = useCallback(() => performSearch(), [performSearch]);

    /**
     * Opens AssignUserToCustomerDialog for OnHold / without-customer members.
     * @type {(function(any): void)}
     */
    const onAssign = useCallback((member) => {
        setAssignDialog({open: true, member});
    }, []);

    /**
     * Confirms and detaches a member from their assigned customer.
     * @type {(function(any): void)}
     */
    const onDetach = useCallback((member) => {
        const memberName = `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim();
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            title: t('confirm.detachTitle'),
            removedEntryTitle: t('confirm.memberEntry', {name: memberName}),
            onClose: async (confirmed) => {
                if (!confirmed) {
                    return true;
                }
                const response = await Api.removeCompanyFromUser(member.id);
                if (!isMounted()) {
                    return true;
                }
                if (response?.resultFlag) {
                    performSearch();
                }
                return response?.resultFlag;
            },
        }));
    }, [dispatch, isMounted, performSearch, t]);

    /**
     * Confirms and deactivates an active member.
     * @type {(function(any): void)}
     */
    const onDeactivate = useCallback((member) => {
        const memberName = `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim();
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            title: t('confirm.deactivateTitle'),
            removedEntryTitle: t('confirm.memberEntry', {name: memberName}),
            onClose: async (confirmed) => {
                if (!confirmed) {
                    return true;
                }
                const response = await Api.toggleUserStatus(member.id);
                if (!isMounted()) {
                    return true;
                }
                if (response?.resultFlag) {
                    performSearch();
                }
                return response?.resultFlag;
            },
        }));
    }, [dispatch, isMounted, performSearch, t]);

    /**
     * Confirms and permanently deletes a member.
     * @type {(function(any): void)}
     */
    const onDelete = useCallback((member) => {
        const memberName = `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim();
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            title: t('confirm.deleteTitle', {name: memberName}),
            onClose: async (confirmed) => {
                if (!confirmed) {
                    return true;
                }
                const response = await Api.removeCustomerMembers({userIds: [member.id]});
                if (!isMounted()) {
                    return true;
                }
                if (response?.resultFlag) {
                    performSearch();
                }
                return response?.resultFlag;
            },
        }));
    }, [dispatch, isMounted, performSearch, t]);

    /**
     * Activates a deactivated member.
     * @type {(function(any): Promise<void>)}
     */
    const onActivate = useCallback(async (member) => {
        const response = await Api.toggleUserStatus(member.id);
        if (!isMounted()) {
            return;
        }
        if (response?.resultFlag) {
            performSearch();
        }
    }, [isMounted, performSearch]);

    /**
     * Loads every member matching the current tab, keyword, and sort.
     * @type {function(): Promise<any[]|null>}
     */
    const fetchAllFilteredMembers = useCallback(async () => {
        const paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        const entries = [];
        while ((searchData.paginationInfo.length ?? 0) > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo}, true);
            if (!res?.resultFlag) {
                return null;
            }
            if (!res?.data?.resultList?.length) {
                break;
            }
            entries.push(...res.data.resultList);
            if (!isMounted()) {
                return null;
            }
            paginationInfo.currentPage++;
        }
        return entries;
    }, [fetchEntries, isMounted, searchData]);

    /**
     * Opens the resend-activation dialog for an On Hold member.
     * @type {(function(any): void)}
     */
    const onResendActivation = useCallback((member) => {
        setResendDialog({open: true, member, members: null});
    }, []);

    /**
     * Resolves the On Hold selection and opens the batch resend dialog.
     * Select-all uses the full filtered result minus exclusions, fetching other pages when needed.
     * @type {(function({selectedRows: any[], excludedRows: any[], allRowsSelected: boolean}): Promise<void>)}
     */
    const onBatchResendActivation = useCallback(async ({selectedRows, excludedRows, allRowsSelected}) => {
        let chosen = [];
        if (!allRowsSelected) {
            chosen = (selectedRows ?? []).map((row) => row.data).filter(Boolean);
        } else {
            const excludedIds = new Set((excludedRows ?? []).map((row) => String(row.key)));
            const total = searchData.paginationInfo?.length ?? 0;
            let allMembers = members;
            if (members.length < total) {
                const fetched = await fetchAllFilteredMembers();
                if (!isMounted() || !fetched) {
                    return;
                }
                allMembers = fetched;
            }
            chosen = allMembers.filter((member) => !excludedIds.has(String(member.id)));
        }
        if (!isMounted() || !chosen.length) {
            return;
        }
        setResendDialog({open: true, member: null, members: chosen});
    }, [fetchAllFilteredMembers, isMounted, members, searchData.paginationInfo?.length]);

    /**
     * Closes the resend-activation dialog.
     * @type {function(): void}
     */
    const closeResendDialog = useCallback(() => {
        setResendDialog((prev) => ({...prev, open: false}));
    }, []);

    const gridSectionProps = useMemo(() => ({
        members,
        searchData,
        countries,
        membershipFallbackTitles,
        onAssign,
        onDetach,
        onDeactivate,
        onActivate,
        onDelete,
        onResendActivation,
        onBatchResendActivation,
        hasB2cWebsite,
        canCreateMember,
        canOpenProfile,
        getExportInfo: canExportCustomerMembersList ? exportToCsv : undefined,
        formLoading,
    }), [
        canExportCustomerMembersList,
        canCreateMember,
        canOpenProfile,
        countries,
        exportToCsv,
        formLoading,
        members,
        membershipFallbackTitles,
        hasB2cWebsite,
        onActivate,
        onAssign,
        onDeactivate,
        onDelete,
        onDetach,
        onBatchResendActivation,
        onResendActivation,
        searchData,
    ]);

    const currentTab = searchData.tab ?? ALL_TAB;
    const isKnownMembershipTab = membershipNames.includes(currentTab);
    const isInactiveTab = currentTab === CustomerMemberShipNames.deactivated
        || currentTab === CustomerMemberShipNames.onHoldCompanyLost;
    const isGenericTab = currentTab !== ALL_TAB
        && !Object.values(CustomerMemberShipNames).includes(currentTab)
        && isKnownMembershipTab;

    const pageActions = useMemo(() => {
        if (!canCreateMember) {
            return null;
        }
        return (
            <PageActions
                items={[{
                    id: 'add-member',
                    label: t('addNew'),
                    icon: <Add className="icon small"/>,
                    onClick: () => setOpenAddUser(true),
                }]}
            />
        );
    }, [canCreateMember, t]);

    const filters = useMemo(() => (
        <SearchContainer>
            <SearchInput
                loading={searchData.loading}
                autoSearch
                immediateSearch
                search={searchByKeyword}
                initialValue={searchData.filters?.keyword ?? undefined}
                label={t('search')}
            />
        </SearchContainer>
    ), [searchByKeyword, searchData.filters?.keyword, searchData.loading, t]);

    /**
     * Resolves the tooltip paragraph for a tab value.
     * @type {(function(string): string)}
     */
    const resolveTabDescription = useCallback((tabValue) => {
        if (tabValue === ALL_TAB) {
            return t('tabs.descriptions.all');
        }
        const knownKeys = {
            [CustomerMemberShipNames.active]: 'tabs.descriptions.Active',
            [CustomerMemberShipNames.onHold]: 'tabs.descriptions.OnHold',
            [CustomerMemberShipNames.deactivated]: 'tabs.descriptions.Deactivated',
            [CustomerMemberShipNames.onHoldCompanyLost]: 'tabs.descriptions.OnHoldCompanyLost',
        };
        if (knownKeys[tabValue]) {
            return t(knownKeys[tabValue]);
        }
        return t('tabs.descriptions.generic', {title: getMembershipTitle(tabValue)});
    }, [getMembershipTitle, t]);

    const tabs = useMemo(() => (
        <Tabs value={currentTab} onChange={onTabChanged} variant="scrollable" scrollButtons="auto">
            <Tab
                value={ALL_TAB}
                label={(
                    <MembersTabLabel
                        label={getMembershipTitle(ALL_TAB)}
                        description={resolveTabDescription(ALL_TAB)}
                        status={ALL_TAB}
                    />
                )}
            />
            {(memberShips ?? []).map((membership) => (
                <Tab
                    key={membership.name}
                    value={membership.name}
                    label={(
                        <MembersTabLabel
                            label={getMembershipTitle(membership.name)}
                            description={resolveTabDescription(membership.name)}
                            status={membership.name}
                        />
                    )}
                />
            ))}
        </Tabs>
    ), [currentTab, getMembershipTitle, memberShips, onTabChanged, resolveTabDescription]);

    return (
        <>
            <ListPageShell
                title={t('title')}
                actions={pageActions}
                filters={filters}
                tabs={tabs}
                loading={{
                    tabs: formLoading,
                    content: formLoading && !membershipNames.length,
                }}
            >
                {
                    currentTab === ALL_TAB &&
                    <MembersAllGridSection {...gridSectionProps}/>
                }
                {
                    currentTab === CustomerMemberShipNames.active &&
                    <MembersActiveGridSection {...gridSectionProps}/>
                }
                {
                    currentTab === CustomerMemberShipNames.onHold &&
                    <MembersOnHoldGridSection {...gridSectionProps}/>
                }
                {
                    isInactiveTab &&
                    <MembersInactiveGridSection key={currentTab} tab={currentTab} {...gridSectionProps}/>
                }
                {
                    isGenericTab &&
                    <MembersGenericGridSection {...gridSectionProps}/>
                }
            </ListPageShell>
            <CreateUserOnlineProfileDialog
                canChangeCompanyInfo
                open={openAddUser}
                setOpen={setOpenAddUser}
                onSuccess={onAddSuccess}
            />
            <AssignUserToCustomerDialog
                countries={countries}
                user={assignDialog.member}
                open={assignDialog.open}
                setOpen={(open) => setAssignDialog((prev) => ({...prev, open}))}
            />
            <ResendActivationDialog
                open={resendDialog.open}
                member={resendDialog.member}
                members={resendDialog.members}
                onClose={closeResendDialog}
            />
        </>
    );
};

export default CustomersMembersListView;
